/**
 * Ciel, lumière et brouillard selon l'heure de jeu et la météo partagée :
 * soleil bas d'hiver (est -> sud -> ouest), crépuscule braise, nuit bleutée,
 * couverture nuageuse qui adoucit la lumière, brouillard plus dense sous la
 * neige. Une seule lumière directionnelle porte les ombres, cadrées autour
 * de la zone regardée.
 */
import * as THREE from 'three/webgpu';
import { cameraPosition, dot, float, max, mix, normalize, positionWorld, pow, smoothstep, uniform } from 'three/tsl';
import type { Weather } from '@snowchain/shared';
import { nightUniform } from './assets';

interface Key {
  m: number;
  zenith: number;
  horizon: number;
  sun: number;
  sunI: number;
  hemiSky: number;
  hemiGround: number;
  hemiI: number;
  night: number;
}

// Nuit : clair de lune bleuté assez fort pour que la neige reste lisible (jeu de gestion) ;
// le tone mapping Neutral retire jusqu'à 0,04 aux tons bas, d'où une ambiance plus haute qu'en réalité.
const NIGHT = { zenith: 0x16283a, horizon: 0x2c4256, sun: 0x9db4d8, sunI: 0.45, hemiSky: 0x5a7aa6, hemiGround: 0x2a3a4c, hemiI: 1.6, night: 1 };

const KEYS: Key[] = [
  { m: 0, ...NIGHT },
  { m: 390, zenith: 0x1b3d4e, horizon: 0x6e7f95, sun: 0xf0b27a, sunI: 0.45, hemiSky: 0x6a86a8, hemiGround: 0x3a4652, hemiI: 1.4, night: 0.85 },
  { m: 480, zenith: 0x4e8ca8, horizon: 0xe9d6c4, sun: 0xffe0b8, sunI: 2.4, hemiSky: 0xbfd7e6, hemiGround: 0xe8e2d6, hemiI: 0.85, night: 0.1 },
  { m: 660, zenith: 0x3e7ea0, horizon: 0xdcebf2, sun: 0xfff4e6, sunI: 3.0, hemiSky: 0xc7dce9, hemiGround: 0xf0ebe1, hemiI: 1.0, night: 0 },
  { m: 900, zenith: 0x4884a4, horizon: 0xe6e0d4, sun: 0xffe7c7, sunI: 2.7, hemiSky: 0xc2d6e3, hemiGround: 0xeee6d8, hemiI: 0.95, night: 0 },
  { m: 1020, zenith: 0x2e5d74, horizon: 0xe89b62, sun: 0xffa86e, sunI: 1.3, hemiSky: 0x8fa9bd, hemiGround: 0xc9b49a, hemiI: 0.9, night: 0.45 },
  { m: 1110, ...NIGHT },
  { m: 1440, ...NIGHT },
];

const tmpA = new THREE.Color();
const tmpB = new THREE.Color();
const GREY = new THREE.Color(0xaab6bd);

function lerpHex(a: number, b: number, t: number, out: THREE.Color): THREE.Color {
  tmpA.setHex(a);
  tmpB.setHex(b);
  return out.copy(tmpA).lerp(tmpB, t);
}

export class SkySystem {
  readonly sun = new THREE.DirectionalLight(0xffffff, 3);
  readonly hemi = new THREE.HemisphereLight(0xc7dce9, 0xf0ebe1, 1);
  readonly fog = new THREE.FogExp2(0xdcebf2, 0.00014);
  readonly dome: THREE.Mesh;
  private readonly zenith = uniform(new THREE.Color(0x3e7ea0));
  private readonly horizon = uniform(new THREE.Color(0xdcebf2));
  private readonly sunDir = uniform(new THREE.Vector3(0, 1, 0));
  private readonly sunColor = uniform(new THREE.Color(0xffffff));
  readonly sunDirection = new THREE.Vector3(0, 1, 0);
  /** 0 en plein jour, 1 la nuit (vitres, lampadaires). */
  night = 0;
  exposure = 1;

  constructor(scene: THREE.Scene) {
    scene.fog = this.fog;
    scene.add(this.hemi);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.6;
    const cam = this.sun.shadow.camera as THREE.OrthographicCamera;
    cam.near = 10;
    cam.far = 2400;
    scene.add(this.sun);
    scene.add(this.sun.target);

    const mat = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide, depthWrite: false });
    mat.fog = false;
    const dir = normalize(positionWorld.sub(cameraPosition));
    const t = smoothstep(-0.04, 0.5, dir.y);
    const sky = mix(this.horizon, this.zenith, t);
    const s = max(dot(dir, this.sunDir), float(0));
    const glow = pow(s, float(9)).mul(0.28).add(pow(s, float(320)).mul(2.2));
    mat.colorNode = sky.add(this.sunColor.mul(glow).mul(smoothstep(-0.08, 0.05, this.sunDir.y)));
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16), mat);
    this.dome.scale.setScalar(11_000);
    this.dome.renderOrder = -1;
    this.dome.frustumCulled = false;
    this.dome.name = 'sky';
    scene.add(this.dome);
  }

  update(minuteOfDay: number, weather: Weather | null, focus: THREE.Vector3, camera: THREE.Camera, shadowSpan: number): void {
    const m = ((minuteOfDay % 1440) + 1440) % 1440;
    let k = 0;
    while (k < KEYS.length - 2 && (KEYS[k + 1] as Key).m <= m) k++;
    const a = KEYS[k] as Key;
    const b = KEYS[k + 1] as Key;
    const t = (m - a.m) / Math.max(1, b.m - a.m);
    const cloud = weather?.cloud ?? 0.3;
    const snowing = (weather?.snowRate ?? 0) > 0.05;

    lerpHex(a.zenith, b.zenith, t, this.zenith.value as THREE.Color).lerp(GREY, cloud * 0.55);
    lerpHex(a.horizon, b.horizon, t, this.horizon.value as THREE.Color).lerp(GREY, cloud * 0.45);
    lerpHex(a.sun, b.sun, t, this.sun.color);
    (this.sunColor.value as THREE.Color).copy(this.sun.color);
    lerpHex(a.hemiSky, b.hemiSky, t, this.hemi.color);
    lerpHex(a.hemiGround, b.hemiGround, t, this.hemi.groundColor);
    this.night = a.night + (b.night - a.night) * t;
    nightUniform.value = this.night;

    // Soleil d'hiver : se lève à l'est-sud-est vers 7 h 40, se couche vers 17 h 20.
    const dayFrac = (m - 450) / (1050 - 450);
    let azimuth: number;
    let elevation: number;
    if (dayFrac > -0.05 && dayFrac < 1.05) {
      azimuth = ((120 + 120 * dayFrac) * Math.PI) / 180;
      elevation = ((20 * Math.sin(Math.PI * Math.min(1, Math.max(0, dayFrac))) + 9) * Math.PI) / 180;
    } else {
      // La lune éclaire la nuit.
      azimuth = (200 * Math.PI) / 180;
      elevation = (38 * Math.PI) / 180;
    }
    this.sunDirection.set(Math.sin(azimuth) * Math.cos(elevation), Math.sin(elevation), -Math.cos(azimuth) * Math.cos(elevation)).normalize();
    (this.sunDir.value as THREE.Vector3).copy(this.sunDirection);

    const intensity = a.sunI + (b.sunI - a.sunI) * t;
    this.sun.intensity = intensity * (1 - 0.7 * cloud) * (snowing ? 0.7 : 1);
    this.hemi.intensity = (a.hemiI + (b.hemiI - a.hemiI) * t) * (1 + cloud * 0.25);
    this.fog.color.copy(this.horizon.value as THREE.Color);
    // La brume est une couche basse : vue de haut, on voit à travers.
    const altitude = Math.max(0, camera.position.y - focus.y - 300);
    this.fog.density = (0.00009 * (1 + cloud * 1.2 + (snowing ? 3 : 0))) / (1 + altitude / 500);
    this.exposure = 1 + this.night * 0.25;

    // Ombres cadrées sur la zone regardée, alignées sur les texels (pas de scintillement).
    const span = Math.max(140, Math.min(800, shadowSpan));
    const cam = this.sun.shadow.camera as THREE.OrthographicCamera;
    if (cam.right !== span) {
      cam.left = -span;
      cam.right = span;
      cam.top = span;
      cam.bottom = -span;
      cam.updateProjectionMatrix();
    }
    const texel = (2 * span) / this.sun.shadow.mapSize.x;
    const fx = Math.round(focus.x / texel) * texel;
    const fz = Math.round(focus.z / texel) * texel;
    this.sun.target.position.set(fx, focus.y, fz);
    this.sun.position.set(fx + this.sunDirection.x * 1200, focus.y + this.sunDirection.y * 1200, fz + this.sunDirection.z * 1200);
    this.sun.target.updateMatrixWorld();
    this.dome.position.copy(camera.position);
  }
}
